import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field", "icon"]
  static values = { app: String, id: String }

  connect() {
    this.timeout = null
    this.tooltipTimeout = null
    this.lastCheckedValue = this.fieldTarget.value.trim()

    this.iconTarget.style.fontSize = "1.2em"

    this.setupTooltipLikeIntTel()

    this.onInputBound = this.onInput.bind(this)
    this.onBlurBound = this.onBlur.bind(this)

    this.fieldTarget.addEventListener("input", this.onInputBound)
    this.fieldTarget.addEventListener("blur", this.onBlurBound)
  }

  disconnect() {
    clearTimeout(this.timeout)
    clearTimeout(this.tooltipTimeout)

    this.fieldTarget.removeEventListener("input", this.onInputBound)
    this.fieldTarget.removeEventListener("blur", this.onBlurBound)
  }

  setupTooltipLikeIntTel() {
    // container = td
    const container = this.element
    if (getComputedStyle(container).position === "static") {
      container.style.position = "relative"
    }

    // trigger = parent (span) avec data-controller="tooltip"
    this.trigger = this.iconTarget.closest('[data-controller~="tooltip"]')
    if (!this.trigger) {
      // fallback : si jamais pas wrap, on crée
      this.trigger = document.createElement("span")
      this.trigger.setAttribute("data-controller", "tooltip")
      this.trigger.style.marginLeft = "8px"
      this.trigger.style.cursor = "pointer"
      this.trigger.style.display = "inline-flex"
      this.trigger.style.alignItems = "center"
      this.iconTarget.parentNode.insertBefore(this.trigger, this.iconTarget)
      this.trigger.appendChild(this.iconTarget)
    }

    const base =
      (this.fieldTarget.id && this.fieldTarget.id.trim()) ||
      (this.fieldTarget.name && this.fieldTarget.name.replace(/\W+/g, "_")) ||
      `email_${Date.now()}`

    // première cible tooltip
    this.tipContent = document.createElement("div")
    this.tipId = `tip_${base}_${Date.now()}`
    this.tipContent.id = this.tipId
    this.tipContent.style.display = "none"
    container.appendChild(this.tipContent)

    this.trigger.setAttribute("data-tooltip-target-id-value", this.tipId)

    this.closeTooltip = () => {
      this.trigger.dispatchEvent(new Event("mouseleave", { bubbles: true }))
      this.iconTarget.dispatchEvent(new Event("mouseleave", { bubbles: true }))
    }

    this.openTooltip = () => {
      this.iconTarget.dispatchEvent(new Event("mouseenter", { bubbles: true }))
      this.trigger.dispatchEvent(new Event("mouseenter", { bubbles: true }))
    }

    this.setTip = (html) => {
      const hovered = this.trigger.matches(":hover") || this.iconTarget.matches(":hover")
      this.closeTooltip()

      const newId = `tip_${base}_${Date.now()}`
      const newContent = document.createElement("div")
      newContent.id = newId
      newContent.style.display = "none"
      newContent.innerHTML = html || ""

      if (this.tipContent.parentNode) {
        this.tipContent.parentNode.replaceChild(newContent, this.tipContent)
      } else {
        container.appendChild(newContent)
      }

      this.tipContent = newContent
      this.tipId = newId
      this.trigger.setAttribute("data-tooltip-target-id-value", this.tipId)

      if (hovered && this.tipContent.textContent.trim() !== "") {
        requestAnimationFrame(this.openTooltip)
      }
    }
  }

  onInput() {
    clearTimeout(this.timeout)
    clearTimeout(this.tooltipTimeout)

    this.timeout = setTimeout(() => {
      this.checkClient(false)
    }, 400)
  }

  onBlur() {
    const currentVal = this.fieldTarget.value.trim()
    if (currentVal !== this.lastCheckedValue) {
      this.checkClient(true)
    } else {
      if (this.tipContent && this.tipContent.textContent.trim()) {
        this.tooltipTimeout = setTimeout(() => this.closeTooltip(), 2000)
      }
    }
  }

  checkClient(fromBlur = false) {
    const val = this.fieldTarget.value.trim()

    if (val.length < 1) {
      this.setNeutral()
      return
    }

    const regex = /\S+@\S+\.\S+/
    if (!regex.test(val)) {
      this.setError("Format invalide", fromBlur)
    } else {
      this.setLoading()
      this.callServer(val, fromBlur)
    }

    this.lastCheckedValue = val
  }

  callServer(email, fromBlur) {
    const appPath = this.appValue || ""
    const contactId = this.idValue || ""
    const url = `${appPath}/contacts_recrutements/verify_email?email=${encodeURIComponent(email)}${
      contactId ? `&id=${contactId}` : ""
    }`

    fetch(url)
      .then((r) => r.json())
      .then((json) => this.handleServerResponse(json, fromBlur))
      .catch(() => this.setError("Erreur serveur", fromBlur))
  }

  handleServerResponse(res, fromBlur) {
    const msgs = []
    if (res.mx === false) msgs.push("MX record manquant")
    if (res.smtp === false) msgs.push("Email introuvable")
    if (res.regex === false) msgs.push("Format invalide")
    if (res.doublon === true) msgs.push("Email en doublon")

    if (res.valid && msgs.length === 0) {
      this.setSuccess("Adresse valide", fromBlur)
      this.dispatch("valid", { detail: { email: this.fieldTarget.value } })
    } else {
      this.setError(msgs.join(", "), fromBlur)
    }
  }

  setLoading() {
    clearTimeout(this.tooltipTimeout)
    this.iconTarget.className = "fa fa-spinner fa-spin"
    this.iconTarget.style.color = "#007bff"
    this.setTip("Vérification en cours...")
    this.openTooltip()
  }

  setSuccess(msg, fromBlur) {
    clearTimeout(this.tooltipTimeout)
    this.iconTarget.className = "fa fa-check-circle"
    this.iconTarget.style.color = "#28a745"
    this.setTip(msg)
    this.openTooltip()

    if (fromBlur) this.tooltipTimeout = setTimeout(() => this.closeTooltip(), 2000)
  }

  setError(msg, fromBlur) {
    clearTimeout(this.tooltipTimeout)
    this.iconTarget.className = "fa fa-exclamation-circle"
    this.iconTarget.style.color = "#dc3545"
    this.setTip(msg)
    this.openTooltip()

    if (fromBlur) this.tooltipTimeout = setTimeout(() => this.closeTooltip(), 2000)
  }

  setNeutral() {
    clearTimeout(this.tooltipTimeout)
    this.iconTarget.className = ""
    this.iconTarget.style.color = ""
    this.setTip("")
    this.closeTooltip()
  }
}
